import React, { Component, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { IntlProvider } from 'react-intl';
import { connect } from 'react-redux';

import AppLocale from 'lngProvider';
import moment from 'moment';

import { THEME_TYPE_DARK } from 'constants/ThemeSetting';
import { locale } from 'util/config';
import { setInitUrl } from 'actions/authActions';
import { authAliasSelector, authTokenSelector, languageSelector } from 'selectors/authSelectors';
import CircularProgress from 'components/ui/CircularProgress';
import { setCompanyLanguage } from 'helpers/companyHelpers';
import { getTheme } from 'selectors/ui/appSelectors';

const AppRoute = lazy(() => import('./AppRoute'));
const SignIn = lazy(() => import('../SignIn'));
const ResetPassword = lazy(() => import('../ResetPassword'));
const FinishedSignUp = lazy(() => import('../FinishedSignUp'));
const UserWizardPage = lazy(() => import('routes/staff/UserWizardPage'));
const UserClientWizardFormContainer = lazy(() => import('containers/user/forms/UserClientWizardFormContainer'));
const PasswordFillingFormContainer = lazy(() => import('containers/user/forms/PasswordFillingFormContainer'));
const SetUpWizardContainer = lazy(() => import('containers/company/SetUpWizardContainer'));
const CompanySignUpContainer = lazy(() => import('containers/company/CompanySignUpContainer'));

const RedirectRoute = lazy(() => import('./RedirectRoute'));

const routeRender = (Component, redirect) => {
  return (meta) =>
    redirect ? <Redirect to={{ pathname: redirect, state: { from: meta.location } }} /> : <Component {...meta} />;
};

class App extends Component {
  componentDidMount() {
    const { location, authToken, authAlias } = this.props;

    // Init locale for moment if use lazy loaded
    const companyLanguage = localStorage.getItem(`${authAlias}-language`);
    const currentLocale = companyLanguage && companyLanguage !== 'undefined' ? companyLanguage : locale.locale;
    if (currentLocale !== 'en') {
      import(`moment/locale/${currentLocale}.js`).then(() => {
        moment.locale(currentLocale);
      });
    }

    if (location.pathname !== '/signin' && !authToken) {
      this.props.setInitUrl(location.pathname + location.search);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.authAlias && prevProps.language !== this.props.language) {
      setCompanyLanguage(this.props.language, this.props.authAlias);
      this.forceUpdate();
    }
  }

  setLayoutType = () => {
    document.body.classList.add('full-layout');
  };

  render() {
    const { authToken, language, theme } = this.props;
    if (theme === THEME_TYPE_DARK) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }

    this.setLayoutType();

    const currentAppLocale = AppLocale[language ? language : locale.locale];
    const redirect = authToken ? '/home' : null;
    const authorize = !authToken ? '/signin' : null;

    return (
      <ConfigProvider locale={currentAppLocale.antd}>
        <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
          <Suspense fallback={<CircularProgress />}>
            <Switch>
              {/*Базовые ресурсы доступные только не авторизованному пользователю*/}
              <Route exact path="/signin" component={routeRender(SignIn, redirect)} />
              <Route exact path="/signup" component={routeRender(CompanySignUpContainer, redirect)} />
              <Route exact path="/reset-password" component={routeRender(ResetPassword, redirect)} />
              <Route exact path="/password-filling" component={routeRender(PasswordFillingFormContainer, redirect)} />

              {/*Ресурсы для подтверждения разных действий приглашения и т.п. доступные всем */}
              <Route exact path="/signup-finished" component={FinishedSignUp} />
              <Route exact path="/set-up-wizard" component={SetUpWizardContainer} />
              <Route exact path="/profile-filling" component={UserWizardPage} />
              <Route exact path="/client/profile-filling" component={UserClientWizardFormContainer} />

              {/* Google.drive и Google.search редиректы на страницы с нужным alias-ом */}
              {/* Для yandex.webmaster редирект делает backend, его нет необходимости здесь обрабатывать */}
              <Route exact path="/projects/project-google-token" component={RedirectRoute} />
              <Route exact path="/company-drive-token" component={RedirectRoute} />

              {/*Ресурсы приложения доступные только авторизованному пользователю*/}
              <Route render={routeRender(AppRoute, authorize)} />
            </Switch>
          </Suspense>
        </IntlProvider>
      </ConfigProvider>
    );
  }
}

function mapStateToProps(state) {
  return {
    authToken: authTokenSelector(state),
    authAlias: authAliasSelector(state),
    language: languageSelector(state),
    theme: getTheme(state),
  };
}

export default connect(mapStateToProps, {
  setInitUrl,
})(App);

App.propTypes = {
  location: PropTypes.object,
  authAlias: PropTypes.string,
  authToken: PropTypes.string,
  language: PropTypes.string,
  theme: PropTypes.string,
  setInitUrl: PropTypes.func,
};
